import React, { useEffect, useState } from "react";
import { Modal, ModalBody } from "react-bootstrap";
import axios from "axios";
import { triggerAlert } from "../../utils";

const INDUSTRIES = [
  "SaaS", "E-commerce", "Healthcare", "Finance", "Education",
  "Real Estate", "Marketing", "Other",
];

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "501+"];

export default function CreateCustomerDialog({ open, onClose, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    login_mode: "invite",
    temp_password: "",
    company_name: "",
    company_size: "",
    industry: "",
    use_case: "",
    coupon_code: "",
    admin_note: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      first_name: "", last_name: "", email: "",
      login_mode: "invite", temp_password: "",
      company_name: "", company_size: "", industry: "", use_case: "",
      coupon_code: "", admin_note: "",
    });
    setErrors({});
  }, [open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "First name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
    if (form.login_mode === "temp_password") {
      if (!form.temp_password) e.temp_password = "Temp password is required";
      else if (form.temp_password.length < 8) e.temp_password = "Min 8 characters";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        company_name: form.company_name.trim(),
        company_size: form.company_size,
        industry: form.industry,
        use_case: form.use_case.trim(),
        send_invite: form.login_mode === "invite",
        coupon_code: form.coupon_code.trim(),
        admin_note: form.admin_note.trim(),
      };
      if (form.login_mode === "temp_password") {
        payload.temp_password = form.temp_password;
      }
      const res = await axios.post("/api/admin/create-customer/", payload);
      triggerAlert("success", "Success", res.data?.message || "Customer created");
      onSuccess && onSuccess(res.data);
      onClose && onClose();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Failed to create customer";
      triggerAlert("error", "Oops...", msg);
    } finally {
      setSubmitting(false);
    }
  };

  const showTempPwd = form.login_mode === "temp_password";

  return (
    <Modal size="lg" show={open} onHide={onClose} className="modal fade" backdrop="static" keyboard={false}>
      <Modal.Header className="modal-header_box bg-my-primary p-3 d-flex justify-content-between" closeButton>
        <h1 className="modal-title fs-5">Create Customer</h1>
      </Modal.Header>
      <form onSubmit={onSubmit}>
        <ModalBody>
          {/* Identity */}
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">First Name <span className="text-danger">*</span></label>
              <input type="text" className="form-control" value={form.first_name}
                onChange={(e) => setField("first_name", e.target.value)} />
              {errors.first_name && <div className="text-danger" style={{ fontSize: 13 }}>{errors.first_name}</div>}
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Last Name</label>
              <input type="text" className="form-control" value={form.last_name}
                onChange={(e) => setField("last_name", e.target.value)} />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label">Email <span className="text-danger">*</span></label>
              <input type="email" className="form-control" value={form.email}
                onChange={(e) => setField("email", e.target.value)} placeholder="customer@example.com" />
              {errors.email && <div className="text-danger" style={{ fontSize: 13 }}>{errors.email}</div>}
            </div>
          </div>

          {/* Login */}
          <h6 className="mt-2 mb-2 text-muted">Login</h6>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label d-block">Mode</label>
              <div className="form-check">
                <input className="form-check-input" type="radio" id="lm_invite" name="login_mode"
                  checked={form.login_mode === "invite"}
                  onChange={() => setField("login_mode", "invite")} />
                <label className="form-check-label" htmlFor="lm_invite">
                  Send invite email <span className="text-muted" style={{ fontSize: 12 }}>(recommended — customer sets their own password)</span>
                </label>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="radio" id="lm_temp" name="login_mode"
                  checked={form.login_mode === "temp_password"}
                  onChange={() => setField("login_mode", "temp_password")} />
                <label className="form-check-label" htmlFor="lm_temp">
                  Set temp password <span className="text-muted" style={{ fontSize: 12 }}>(no email; share creds out-of-band)</span>
                </label>
              </div>
            </div>
            {showTempPwd && (
              <div className="col-md-6 mb-3">
                <label className="form-label">Temp Password <span className="text-danger">*</span></label>
                <input type="text" className="form-control" value={form.temp_password}
                  onChange={(e) => setField("temp_password", e.target.value)} placeholder="Min 8 chars" />
                {errors.temp_password && <div className="text-danger" style={{ fontSize: 13 }}>{errors.temp_password}</div>}
                <small className="text-muted">Customer will log in with this; they can change it later via Forgot Password.</small>
              </div>
            )}
          </div>

          {/* Org */}
          <h6 className="mt-2 mb-2 text-muted">Organization</h6>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Company Name</label>
              <input type="text" className="form-control" value={form.company_name}
                onChange={(e) => setField("company_name", e.target.value)}
                placeholder={`Defaults to "${form.first_name || "First"}'s Organization"`} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Company Size</label>
              <select className="form-control" value={form.company_size}
                onChange={(e) => setField("company_size", e.target.value)}>
                <option value="">— optional —</option>
                {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">Industry</label>
              <select className="form-control" value={form.industry}
                onChange={(e) => setField("industry", e.target.value)}>
                <option value="">— optional —</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label">Use Case</label>
              <input type="text" className="form-control" value={form.use_case}
                onChange={(e) => setField("use_case", e.target.value)}
                placeholder="What will they build? (optional)" />
            </div>
          </div>

          {/* Optional admin grants */}
          <h6 className="mt-2 mb-2 text-muted">Optional Admin Grants</h6>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Coupon Code</label>
              <input type="text" className="form-control" value={form.coupon_code}
                onChange={(e) => setField("coupon_code", e.target.value)} />
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-2">
              <label className="form-label">Admin Note</label>
              <textarea rows={2} className="form-control" value={form.admin_note}
                onChange={(e) => setField("admin_note", e.target.value)}
                placeholder="Internal-only note (appears in audit log)" />
            </div>
          </div>
        </ModalBody>
        <Modal.Footer>
          <button type="button" className="btn btn-warning w-md btn-md me-2"
            onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="submit" className="btn btn-primary w-md btn-md" disabled={submitting}>
            {submitting ? "Creating..." : "Create Customer"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
